import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/20/solid";
import { ago, NoteEntityId, NoteId } from "@sp-crm/core";
import { AuthorName } from "components/byline/author-name";
import { EditNote } from "components/notes/edit";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { Spinner } from "components/ui/spinner";
import { UpdateNoteMutation } from "generated/graphql";
import React from "react";
import { breakNewlines } from "util/text";
import { NoteRowNote, useNoteRow } from "./use-note-row";

interface Props {
    note: NoteRowNote;
    refetch: () => void;
    entityId: NoteEntityId;
    pinnedNoteId: NoteId | null;
    onDeleted: (noteId: NoteId) => void;
    onUpdated: (note: UpdateNoteMutation["updateNote"]) => void;
}

export const ActivityFeedNote: React.FC<Props> = props => {
    const { note, entityId, pinnedNoteId, refetch, onDeleted, onUpdated } = props;
    const {
        isEditing,
        isDeleting,
        enableEdit,
        disableEdit,
        deleteCallback,
        updateCallback,
        editableNote,
    } = useNoteRow({ note, entityId, refetch, onDeleted, onUpdated });
    return (
        <div className="relative flex items-start space-x-3">
            <div>
                <div className="relative px-1">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 ring-8 ring-white">
                        <ChatBubbleLeftEllipsisIcon
                            className="h-5 w-5 text-gray-500"
                            aria-hidden="true"
                        />
                    </div>
                </div>
            </div>
            <div className="min-w-0 flex-1 group">
                {isEditing ? (
                    <EditNote
                        onEditNote={updateCallback}
                        note={editableNote}
                        pinnedNoteId={pinnedNoteId}
                        leaveEditMode={disableEdit}
                    />
                ) : (
                    <div className="space-y-2">
                        <div>
                            <div className="text-sm flex items-center space-x-2">
                                <span className="font-medium text-gray-900">
                                    <AuthorName authorId={note.authorId} />
                                </span>
                                {note.isPinned ? (
                                    <Badge type="highlight" value="Important" />
                                ) : null}
                            </div>
                            <p className="mt-0.5 text-sm text-gray-500">
                                {ago(note.dateAdded, "")}
                                {note.wasEdited ? " (edited)" : ""}
                            </p>
                        </div>
                        <div className="text-base text-gray-900">
                            <p>{breakNewlines(note.text)}</p>
                        </div>
                        {isDeleting ? (
                            <div className="flex space-x-2 items-center">
                                <span className="text-sm italic text-gray-600">
                                    Deleting...
                                </span>
                                <Spinner />
                            </div>
                        ) : (
                            <div className="flex space-x-2 text-gray-300 group-hover:text-gray-700">
                                <EditButton
                                    backgroundColor="bg-gray-200"
                                    onClick={enableEdit}
                                />

                                <DeleteButton
                                    confirm={{
                                        title: "Delete note?",
                                        message:
                                            "Are you sure you want to delete this note?",
                                    }}
                                    backgroundColor="bg-gray-200"
                                    onClick={deleteCallback}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
